import { useEffect, useMemo, useRef, useState } from 'react';
import { useCustom, useCustomMutation, useList } from '@refinedev/core';
import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react';
import { notify } from '@/lib/notify';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { resolveApiUrl } from '@/providers/api-url';
import type { TagItem } from '@/resources/tags';
import type { ContentTagTargetType, ContentTagsResponse } from '@/resources/content-tags/types';

const CONTENT_TAGS_URL = resolveApiUrl() + '/admin/content-tags';

interface ContentTagsFieldProps {
  entityType: ContentTagTargetType;
  entityId: number | null;
  disabled?: boolean;
  className?: string;
  label?: string;
  description?: string;
}

interface TagIdsHolder {
  tagIds: number[];
}

interface DataWrapper {
  data: unknown;
}

function hasTagIds(value: unknown): value is TagIdsHolder {
  if (typeof value !== 'object' || value === null) return false;
  if (!('tagIds' in value)) return false;
  const ids = Reflect.get(value, 'tagIds');
  return Array.isArray(ids) && ids.every((id) => typeof id === 'number');
}

function hasDataField(value: unknown): value is DataWrapper {
  return typeof value === 'object' && value !== null && 'data' in value;
}

function extractTagIds(payload: unknown): number[] {
  if (hasTagIds(payload)) return payload.tagIds;
  if (hasDataField(payload) && hasTagIds(payload.data)) return payload.data.tagIds;
  return [];
}

/**
 * Multi-select назначение тегов любой контент-сущности.
 * GET /admin/content-tags?entityType&entityId возвращает текущий набор tagIds,
 * PUT /admin/content-tags { entityType, entityId, tagIds } делает replace.
 *
 * Видимость контента определяется пересечением этих тегов с UserTag юзера.
 */
export function ContentTagsField({
  entityType,
  entityId,
  disabled = false,
  className,
  label = 'Теги (видимость)',
  description = 'Видят пользователи с пересечением тегов. Пусто = виден всем.',
}: ContentTagsFieldProps) {
  const [open, setOpen] = useState(false);
  const [localTagIds, setLocalTagIds] = useState<number[]>([]);
  const syncedKeyRef = useRef<string | null>(null);
  const pendingSaveRef = useRef(false);

  const tagsList = useList<TagItem>({
    resource: 'tags',
    pagination: { pageSize: 500, currentPage: 1, mode: 'server' },
    queryOptions: { staleTime: 30_000 },
  });
  const allTags = useMemo(
    () => (tagsList.query.data?.data ?? []).filter((t) => t.deletedAt === null),
    [tagsList.query.data],
  );
  const tagsById = useMemo(() => new Map(allTags.map((t) => [t.id, t])), [allTags]);

  const enabled = entityId !== null && entityId > 0;
  const entityKey = enabled ? `${entityType}:${entityId}` : null;

  useEffect(() => {
    if (syncedKeyRef.current !== entityKey) {
      syncedKeyRef.current = null;
      setLocalTagIds([]);
    }
  }, [entityKey]);

  const currentTags = useCustom<ContentTagsResponse>({
    url: CONTENT_TAGS_URL,
    method: 'get',
    config: {
      query: enabled ? { entityType, entityId } : {},
    },
    queryOptions: { enabled, staleTime: 0, refetchOnMount: 'always' },
  });

  useEffect(() => {
    if (!enabled || entityKey === null) return;
    if (pendingSaveRef.current) return;
    const payload = currentTags.query.data?.data;
    if (payload === undefined) return;
    setLocalTagIds(extractTagIds(payload));
    syncedKeyRef.current = entityKey;
  }, [enabled, entityKey, currentTags.query.data]);

  const mutation = useCustomMutation();

  const handleSave = async (nextIds: number[]) => {
    if (!enabled) return;
    const previous = localTagIds;
    setLocalTagIds(nextIds);
    pendingSaveRef.current = true;
    try {
      await mutation.mutateAsync({
        url: CONTENT_TAGS_URL,
        method: 'put',
        values: { entityType, entityId, tagIds: nextIds },
        successNotification: false,
        errorNotification: false,
      });
      notify.success('Теги сохранены');
    } catch (err) {
      notify.error('Ошибка сохранения тегов');
      setLocalTagIds(previous);
      console.error(err);
    } finally {
      pendingSaveRef.current = false;
    }
  };

  const toggle = (tagId: number) => {
    const set = new Set(localTagIds);
    if (set.has(tagId)) set.delete(tagId);
    else set.add(tagId);
    void handleSave(Array.from(set));
  };

  const remove = (tagId: number) => {
    void handleSave(localTagIds.filter((id) => id !== tagId));
  };

  const isDisabled = disabled || !enabled || mutation.mutation.isPending;
  const selectedTags = localTagIds
    .map((id) => tagsById.get(id))
    .filter((t): t is TagItem => Boolean(t));

  return (
    <div className={cn('space-y-2', className)}>
      <div>
        <div className="text-sm font-medium">{label}</div>
        {description && (
          <div className="text-xs text-muted-foreground">{description}</div>
        )}
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={isDisabled}
            className={cn(
              'w-full justify-between font-normal h-auto min-h-9',
              selectedTags.length === 0 && 'text-muted-foreground',
            )}
          >
            <div className="flex flex-wrap gap-1 flex-1">
              {mutation.mutation.isPending && selectedTags.length === 0 ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Сохранение...
                </span>
              ) : selectedTags.length === 0 ? (
                <span>Добавить тег</span>
              ) : (
                selectedTags.map((tag) => (
                  <Badge
                    key={tag.id}
                    variant="secondary"
                    className="text-xs px-1.5 py-0 gap-1.5"
                    style={{ borderColor: tag.color }}
                  >
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    {tag.name}
                    {!isDisabled && (
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label={`Убрать тег ${tag.name}`}
                        className="ml-1 inline-flex items-center justify-center rounded-full outline-none hover:bg-muted-foreground/20 cursor-pointer"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          remove(tag.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            remove(tag.id);
                          }
                        }}
                      >
                        <X className="h-3 w-3" />
                      </span>
                    )}
                  </Badge>
                ))
              )}
            </div>
            {mutation.mutation.isPending && selectedTags.length > 0 ? (
              <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin opacity-70" />
            ) : (
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Поиск тега..." />
            <CommandList>
              <CommandEmpty>Теги не найдены</CommandEmpty>
              <CommandGroup>
                {allTags.map((tag) => {
                  const checked = localTagIds.includes(tag.id);
                  return (
                    <CommandItem
                      key={tag.id}
                      value={`${tag.name}-${tag.id}`}
                      onSelect={() => toggle(tag.id)}
                    >
                      <Check
                        className={cn('mr-2 h-4 w-4', checked ? 'opacity-100' : 'opacity-0')}
                      />
                      <span
                        className="h-3 w-3 rounded-full mr-2 flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
